import React from "react";
import styled from "styled-components";
import { BadgeStatus } from "../../../enums/Status";
import {
  Card,
  FlexibleContainer,
  RowContainer,
} from "../../../components/containers/styledComponents";
import {
  boxShadow,
  CompassParagraph,
} from "../../../components/common/styledComponents";
import { greyText, lightGreys, statusBadgeColors } from "../../../styles";
import { formatDateShort } from "../../../utils/formattingUtils";
import { MESSAGES } from "../../../messages";
import {
  IconContainer,
  Icon,
  LegendCircle,
} from "../../../components/visuals/styledComponents";
import m1 from "../../../assets/svg/m4_icon.svg";
import m2 from "../../../assets/svg/m2_icon.svg";
import m3 from "../../../assets/svg/m3_icon.svg";
import m4 from "../../../assets/svg/m1_icon.svg";
import calendarIcon from "../../../assets/svg/calendar_icon.svg";

export const MILESTONE_ICONS = [m1, m2, m3, m4];

const BadgeContainer = styled(FlexibleContainer)<{
  color?: string;
  hasStatus?: boolean;
}>`
  display: flex;
  height: 28px;
  justify-content: center;
  align-items: center;
  text-align: center;
  margin-right: 5px;
  background-color: ${lightGreys.KPIBackground};
  border-radius: 12px;
  padding: 6px 12px;
`;

const CardContent = styled.div`
  height: 100%;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
`;

const DescriptionContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-self: stretch;
  height: 125px;
  overflow-y: auto;
`;

export enum MilestoneStep {
  M1 = "Raising Concerns",
  M2 = "Acknowledging Concerns",
  M3 = "Resolution Planning",
  M4 = "Implementing",
}
export type MilestoneItem = {
  milestoneId: number;
  milestone: MilestoneStep;
  description: string;
  startDate: string; // ISO date
  status: BadgeStatus;
  lastCompletionDate: string; // ISO date
};

export const MilestoneCard: React.FC<{ milestone: MilestoneItem }> = ({
  milestone,
}) => {
  return (
    <Card
      h="250px"
      wpx="400px"
      p="15px"
      style={{
        border: `1px solid ${lightGreys.borderLightGrey}`,
        boxShadow: boxShadow,
      }}
    >
      <CardContent>
        <FlexibleContainer fd="column" jc="flex-start">
          <RowContainer mt={1} ai="center" jc="flex-start">
            <IconContainer m={1} p={1} size={32}>
              <Icon src={MILESTONE_ICONS[milestone.milestoneId - 1]} />
            </IconContainer>
            <CompassParagraph fontSize={13} ml={4}>
              Milestone {milestone.milestoneId}:
            </CompassParagraph>
            <CompassParagraph fontSize={13}>
              {milestone.milestone}
            </CompassParagraph>
          </RowContainer>

          <RowContainer mt={1} ai="center" jc="flex-start">
            <LegendCircle color="transparent" size={26} />
            <DescriptionContainer>
              <CompassParagraph color={greyText} ta="justify">
                {milestone.description || "—"}
              </CompassParagraph>
            </DescriptionContainer>
          </RowContainer>
        </FlexibleContainer>
        <RowContainer ai="center" jc="flex-end">
          <SmallRoundedBadge
            text={
              formatDateShort(milestone.startDate) || MESSAGES.NOT_AVAILABLE
            }
          />
          <SmallRoundedBadge
            text={
              formatDateShort(milestone.lastCompletionDate) ||
              MESSAGES.NOT_AVAILABLE
            }
            status={milestone.status}
          />
        </RowContainer>
      </CardContent>
    </Card>
  );
};

const SmallRoundedBadge: React.FC<{ text: string; status?: BadgeStatus }> = ({
  text,
  status = null,
}): JSX.Element => {
  const badgeColors = status ? statusBadgeColors[status] : null;
  return (
    <BadgeContainer hasStatus={!!status} color={badgeColors?.bg || "white"}>
      {!status ? (
        <IconContainer p={1} m={1} size={16}>
          <Icon src={calendarIcon} />
        </IconContainer>
      ) : (
        <LegendCircle
          color={badgeColors?.text || statusBadgeColors.Planned.text}
        />
      )}
      <CompassParagraph
        fontSize={11}
        bold={true}
        color={greyText}
        ml={2}
        mt={2}
      >
        {status ? `${status}: ` : "Start: "}
        {text}
      </CompassParagraph>
    </BadgeContainer>
  );
};
